import type { ErrorStreamEvent, PartialMessageStreamEvent, StreamEvent } from "./types";

type ParseOptions = {
    onEvent: (event: StreamEvent) => void;
};

const isRecord = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object";

const createParseEventStreamService = () => {
    const isErrorStreamEvent = (event: StreamEvent): event is ErrorStreamEvent => {
        return event && typeof event === "object" && event.type === "error" && typeof event.data === "string";
    };

    const isPartialMessageStreamEvent = (event: StreamEvent): event is PartialMessageStreamEvent => {
        return (
            event &&
            typeof event === "object" &&
            event.type === "messages/partial" &&
            Array.isArray(event.data) &&
            isRecord(event.data[0]) &&
            typeof event.data[0].content === "string"
        );
    };

    const parseEventStream = async (response: Response, { onEvent }: ParseOptions) => {
        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error("No response body available for streaming");
        }

        const decoder = new TextDecoder();
        let buffer = "";
        let currentEvent: string | null = null;
        let currentData: string[] = [];

        const processBufferedEvent = () => {
            if (!currentEvent || currentData.length === 0) {
                return;
            }

            const raw = currentData.join("\n");
            let parsedData: unknown;

            try {
                parsedData = JSON.parse(raw);
            } catch {
                parsedData = raw;
            }

            onEvent({ type: currentEvent, data: parsedData });

            currentEvent = null;
            currentData = [];
        };

        while (true) {
            const { value, done } = await reader.read();
            if (done) {
                processBufferedEvent();
                break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
                if (line.startsWith("event:")) {
                    currentEvent = line.slice("event:".length).trim();
                } else if (line.startsWith("data:")) {
                    currentData.push(line.slice("data:".length).trim());
                } else if (line.trim() === "") {
                    processBufferedEvent();
                }
            }
        }
    };

    return {
        parseEventStream,
        isErrorStreamEvent,
        isPartialMessageStreamEvent,
    };
};

const ParseEventStreamService = createParseEventStreamService();

export default ParseEventStreamService;
